import React, { ReactNode, useEffect, useRef, useState } from 'react'; // Add ReactNode for JSX labels
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink'; // Reuse the ImageLink component for displaying the file link
import {
  AuditLogDTO,
  EmailMessageDTO,
  MemberDisciplinaryStatusDTO,
  MemberDTO,
  MemberStatusDTO,
  MemberTerminationStatusDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useCountries } from '../../hooks/useCountryApi';
import { useLanguages } from '../../hooks/useLanguageApi';
import { useMemberCategories } from '../../hooks/useMemberCategoryApi';
import { useMemberTypes } from '../../hooks/useMemberTypeApi';
import { useMemberTitles } from '../../hooks/useMemberTitleApi';
import { useMemberStatuses } from '../../hooks/useMemberStatusApi';
import { useMemberIdTypes } from '../../hooks/useMemberIdTypeApi';
import { useGenders } from '../../hooks/useGenderApi';
import { useMemberDisciplinaryStatuses } from '../../hooks/useMemberDisciplinaryStatusApi';
import { useMemberTerminationStatuses } from '../../hooks/useMemberTerminationStatusApi';
import { useMemberRegistrationTypes } from '../../hooks/useMemberRegistrationTypeApi';
import { useMemberRegistrationSteps } from '../../hooks/useMemberRegistrationStepApi';
import { useMemberReferralTypes } from '../../hooks/useMemberReferralTypeApi';
import { useFileApi } from '../../hooks/useFileApi'; // Import the file API hook
import Button from '../common/Button';
import { useAuditLogApi } from '../../hooks/useAuditLogApi';
import AuditLogTable from '../tables/AuditLogTable';
import ChangePasswordDrawer from '../drawer/ChangePasswordDrawer';
import { Key } from 'lucide-react';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import { usePaymentTypes } from '../../hooks/usePaymentTypeApi';
import { CountryConstant } from '../../constants/CountryConstants';
import { ProvinceConstant } from '../../constants/ProvinceConstants';
import { provinceLogic } from '../../utils/provinceLogic';
import EmailMessageItem from '../common/formElements/EmailMessageItem';
import { useEmailMessageApi } from '../../hooks/useEmailMessageApi';
import EmailMessageItemHeader from '../common/formElements/EmailMessageItemHeader';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import Pagination from '../common/Pagination';
import Card from '../common/Card';

interface MemberFormProps {
  initialValues: MemberDTO;
  onSubmit: (data: MemberDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  member: {
    id: 'member',
    title: 'Member',
  },
  contact: {
    id: 'contact',
    title: 'Contact',
  },
  status: {
    id: 'status',
    title: 'Status',
  },
  paymentDetails: {
    id: 'payment-details',
    title: 'Payment Details',
  },
  disciplinary: {
    id: 'disciplinary',
    title: 'Disciplinary',
  },
  general: {
    id: 'general',
    title: 'General',
  },
  history: {
    id: 'history',
    title: 'History',
  },
  pastEmails: {
    id: 'past-emails',
    title: 'Past Emails',
  },
} as const;

const MemberForm: React.FC<MemberFormProps> = ({ initialValues, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<MemberDTO>(initialValues);

  // File API hook
  const { uploadFile, getFileUrl } = useFileApi();

  const { provinceOptions, isLoadingProvinces, shouldShowProvinceOther } = provinceLogic({
    countryId: formData.countryId,
    provinceId: formData.provinceId,
    provinceOther: formData.provinceOther,
    isExistingMember: !!initialValues.id,
    autoSetNotOnList: true,
    onProvinceChange: (newProvinceId, newProvinceOther) => {
      setFormData((prev) => ({
        ...prev,
        provinceId: newProvinceId,
        provinceOther: newProvinceOther,
      }));
    },
  });

  // Define the type for dropdown options
  type DropdownOption = { value: string; label: ReactNode }; // Updated label type to ReactNode

  // Dropdown options with explicit types

  const [emailMessages, setEmailMessages] = useState<EmailMessageDTO[]>([]);
  const [emailCurrentPage, setEmailCurrentPage] = useState(1);
  const [emailTotalPages, setEmailTotalPages] = useState(1);
  const [emailPageSize, setEmailPageSize] = useState(5);
  const emailSectionsRef = useRef<HTMLDivElement>(null);

  const validationRules: ValidationRules = {
    // Member Section
    memberTitleId: [{ required: true, message: 'Title is required' }],
    firstName: [{ required: true, message: 'First Name is required' }],
    lastName: [{ required: true, message: 'Last Name is required' }],
    genderId: [{ required: true, message: 'Gender is required' }],
    languageId: [{ required: true, message: 'Language is required' }],
    // Contact Section
    email: [{ required: true, message: 'Email is required' }],
    countryId: [{ required: true, message: 'Country is required' }],
    provinceId: [
      {
        required: formData.countryId === CountryConstant.SOUTH_AFRICA,
        message: 'Province is required',
      },
    ],
    provinceOther: [
      {
        required: formData.countryId !== CountryConstant.SOUTH_AFRICA,
        message: 'Province Other is required',
      },
    ],
    // Status Section
    memberStatusId: [{ required: true, message: 'Member Status is required' }],
    // Disciplinary Section
    memberDisciplinaryStatusId: [{ required: true, message: 'Disciplinary Status is required' }],
    memberTerminationStatusId: [{ required: true, message: 'Termination Status is required' }],

  };

  const { errors, validate } = useFormValidation<MemberDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      const submissionData = { ...formData };

      if (submissionData.provinceId === ProvinceConstant.NOT_ON_LIST) {
        submissionData.provinceId = undefined;
      }

      onSubmit(submissionData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  const { data: countries, isLoading: isLoadingCountries } = useCountries();
  const { data: languages, isLoading: isLoadingLanguages } = useLanguages();
  const { data: memberTypesData, isLoading: isLoadingMemberTypes } = useMemberTypes();
  const { data: gendersData, isLoading: isLoadingGenders } = useGenders();
  const { data: disciplinaryStatusesData, isLoading: isLoadingDisciplinaryStatuses } = useMemberDisciplinaryStatuses();
  const { data: titlesData, isLoading: isLoadingTitles } = useMemberTitles();
  const { data: idTypesData, isLoading: isLoadingIdTypes } = useMemberIdTypes();
  const { data: memberStatusesData, isLoading: isLoadingMemberStatuses } = useMemberStatuses();
  const { data: memberCategoriesData, isLoading: isLoadingMemberCategories } = useMemberCategories();
  const { data: terminationStatusesData, isLoading: isLoadingTerminationStatuses } =
    useMemberTerminationStatuses();
  const { data: registrationTypesData, isLoading: isLoadingRegistrationTypes } =
    useMemberRegistrationTypes();
  const { data: registrationStepsData, isLoading: isLoadingRegistrationSteps } =
    useMemberRegistrationSteps();
  const { data: referralTypesData, isLoading: isLoadingReferralTypes } = useMemberReferralTypes();
  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = usePaymentTypes();
  const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } = useAuditLogApi();
  const { search: searchEmailMessages, isLoading: isLoadingEmailMessages } =
    useEmailMessageApi();

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});


  // Add state for audit logs
  const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);

  // Load audit logs when page changes
  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (initialValues.id) {
        try {
          const response = await searchAuditLogs({
            page: auditCurrentPage,
            pageSize: auditPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
            filters: {
              tableName: 'Member',
              recordId: initialValues.id,
            },
          });

          if (response && response?.success && response?.data) {
            setAuditLogs(response.data.results as AuditLogDTO[]);
            setAuditTotalPages(Math.ceil((response.data.totalCount ?? 0) / auditPageSize));
          }
        } catch (error) {
          console.error('Error fetching audit logs:', error);
        }
      }
    };

    fetchAuditLogs();
  }, [initialValues.id, auditCurrentPage, auditPageSize]);

  useEffect(() => {
    const fetchEmailMessages = async () => {
      if (initialValues.id) {
        try {
          const response = await searchEmailMessages({
            filters: {
              memberId: formData.id,
            },
            page: emailCurrentPage,
            pageSize: emailPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
          });
          setEmailMessages(response?.data?.results ?? []);
          setEmailTotalPages(Math.ceil((response?.data?.totalCount ?? 0) / emailPageSize));
        } catch (error) {
          console.error('Error fetching email messages:', error);
        }
      }
    };

    fetchEmailMessages();
  }, [initialValues.id, emailCurrentPage, emailPageSize]);

  const handleFileChange =
    (field: keyof MemberDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          setUploadingFiles((prev) => ({ ...prev, [field]: true }));
          const response = await uploadFile('files', file.name, file, 'members');
          if (response && response.success && response.data) {
            setFormData((prev) => ({
              ...prev,
              [field]: response.data, // Save the file ID
            }));
          }
        } catch (error) {
          console.error('Error uploading file:', error);
        } finally {
          setUploadingFiles((prev) => ({ ...prev, [field]: false }));
        }
      }
    };

  // Add functions to handle copying and clearing courier details
  const copyPhysicalToCourier = () => {
    setFormData((prev) => ({
      ...prev,
      courierAddress: prev.physicalAddress || '',
      courierTown: prev.physicalTown || '',
      courierSuburb: prev.physicalSuburb || '',
      courierPostalCode: prev.physicalPostalCode || '',
    }));
  };

  const clearCourierDetails = () => {
    setFormData((prev) => ({
      ...prev,
      courierAddress: '',
      courierTown: '',
      courierSuburb: '',
      courierPostalCode: '',
    }));
  };

  const handleAuditPageChange = (page: number) => {
    setAuditCurrentPage(page);
  };

  const handleAuditPageSizeChange = (pageSize: number) => {
    setAuditPageSize(pageSize);
    setAuditCurrentPage(1); // Reset to first page on page size change
  };

  const handleEmailPageChange = (page: number) => {
    setEmailCurrentPage(page);
    // Scroll to the first email section
    emailSectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleEmailPageSizeChange = (pageSize: number) => {
    setEmailPageSize(pageSize);
    setEmailCurrentPage(1); // Reset to first page on page size change
    // Scroll to the first email section
    emailSectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Add state for password change drawer
  const [isPasswordDrawerOpen, setIsPasswordDrawerOpen] = useState(false);

  return (
    <>
      <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
        <FormSection
          {...FORM_SECTIONS.member}
          headerActions={
            initialValues.id ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsPasswordDrawerOpen(true)}
                className="flex items-center space-x-2"
              >
                <Key size={16} />
                <span>Change Password</span>
              </Button>
            ) : null
          }
        >
          <FormField
            id="memberTypeId"
            label="Member Type"
            type="dropdown"
            value={formData.memberTypeId || ''}
            onChange={(e) => setFormData({ ...formData, memberTypeId: e.target.value })}
            options={(memberTypesData || []).map((mt) => ({ value: mt.id ?? '', label: mt.title ?? '' }))}
            isLoading={isLoadingMemberTypes}
          />
          <FormField
            id="memberCategoryId"
            label={`FCA Status of ${formData.firstName ? formData.firstName : 'Member'}`}
            type="dropdown"
            value={formData.memberCategoryId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Member Category (FCA Status)"
            options={(memberCategoriesData || []).map((mc) => ({
              value: mc.id ?? '',
              label: mc.title ?? '',
            }))}
            isLoading={isLoadingMemberCategories}
          />
          <FormField
            id="memberTitleId"
            label="Title"
            type="dropdown"
            value={formData.memberTitleId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Member Title"
            required={true}
            error={errors.memberTitleId}
            options={(titlesData || []).map((t) => ({
              value: t.id ?? '',
              label: t.title ?? '',
            }))}
            isLoading={isLoadingTitles}
          />
          <FormField
            id="initials"
            label="Initials"
            type="text"
            value={formData.initials || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Initials"
            required={false}
            error={errors.initials}
          />
          <FormField
            id="firstName"
            label="First Name"
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter First Name"
            required={true}
            error={errors.firstName}
            textCase="titlecase"
          />
          <FormField
            id="lastName"
            label="Last Name"
            type="text"
            value={formData.lastName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Last Name"
            required={true}
            error={errors.lastName}
            textCase="titlecase"
          />
          <FormField
            id="dateOfBirth"
            label="Date Of Birth"
            type="date"
            value={formData.dateOfBirth || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Date Of Birth"
            required={false}
            error={errors.dateOfBirth}
          />
          <FormField
            id="memberIdTypeId"
            label="Id Type"
            type="dropdown"
            value={formData.memberIdTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Id Type"
            required={false}
            error={errors.memberIdTypeId}
            options={(idTypesData || []).map((it) => ({
              value: it.id ?? '',
              label: it.title ?? '',
            }))}
            isLoading={isLoadingIdTypes}
          />
          <FormField
            id="idPassportNumber"
            label="Id/Passport Number"
            type="text"
            value={formData.idPassportNumber || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Id Passport Number"
            required={false}
            error={errors.idPassportNumber}
          />
          <FormField
            id="genderId"
            label="Gender"
            type="dropdown"
            value={formData.genderId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Gender"
            required={true}
            error={errors.genderId}
            options={(gendersData || []).map((g) => ({ value: g.id ?? '', label: g.title ?? '' }))}
            isLoading={isLoadingGenders}
          />
          <FormField
            id="languageId"
            label="Language"
            type="dropdown"
            value={formData.languageId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Language"
            required={true}
            error={errors.languageId}
            options={(languages || []).map((l) => ({
              value: l.id ?? '',
              label: l.title ?? '',
            }))}
            isLoading={isLoadingLanguages}
          />
          <FormField
            id="languageOther"
            label="Language Other"
            type="text"
            value={formData.languageOther || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Language Other"
            required={false}
            error={errors.languageOther}
          />
          <FormField
            id="companyName"
            label="Company Name"
            type="text"
            value={formData.companyName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Company Name"
            required={false}
            error={errors.companyName}
          />
          <FormField
            id="companyVat"
            label="Company Vat"
            type="text"
            value={formData.companyVat || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Company Vat"
            required={false}
            error={errors.companyVat}
          />
        </FormSection>

        <FormSection id={FORM_SECTIONS.contact.id} title={FORM_SECTIONS.contact.title}>
          <FormField
            id="email"
            label="Email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Email"
            textCase='lowercase'
            required={true}
            error={errors.email}
          />
          <FormField
            id="phone"
            label="Cell Phone"
            type="text"
            value={formData.phone || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Cellphone Number"
            required={false}
            error={errors.phone}
          />
          <FormField
            id="landlinePhone"
            label="Telephone"
            type="text"
            value={formData.landlinePhone || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Telephone"
            required={false}
            error={errors.landlinePhone}
          />
          <FormField
            id="officePhone"
            label="Office Phone"
            type="text"
            value={formData.officePhone || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Office Phone"
            required={false}
            error={errors.officePhone}
          />
          <FormField
            id="countryId"
            label="Country"
            type="dropdown"
            value={formData.countryId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Country"
            required={true}
            error={errors.countryId}
            options={(countries || []).map((c) => ({ value: c.id ?? '', label: c.title ?? '' }))}
            isLoading={isLoadingCountries}
          />
          <FormField
            id="provinceId"
            label="Province"
            type="dropdown"
            value={formData.provinceId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Province"
            required={formData.countryId === CountryConstant.SOUTH_AFRICA || !formData.countryId}
            error={errors.provinceId}
            options={provinceOptions}
            isLoading={isLoadingProvinces}
          />
          {shouldShowProvinceOther && (
            <FormField
              id="provinceOther"
              label="Province Other"
              type="text"
              value={formData.provinceOther || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Province Other"
              required={shouldShowProvinceOther}
              error={errors.provinceOther}
            />
          )}
          <FormField
            id="physicalAddress"
            label="Physical Address"
            type="text"
            value={formData.physicalAddress || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Physical Address"
            required={false}
            error={errors.physicalAddress}
          />
          <FormField
            id="physicalTown"
            label="Physical Town"
            type="text"
            value={formData.physicalTown || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Physical Town"
            required={false}
            error={errors.physicalTown}
          />
          <FormField
            id="physicalSuburb"
            label="Physical Suburb"
            type="text"
            value={formData.physicalSuburb || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Physical Suburb"
            required={false}
            error={errors.physicalSuburb}
          />
          <FormField
            id="physicalPostalCode"
            label="Physical Postal Code"
            type="text"
            value={formData.physicalPostalCode || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Physical Postal Code"
            required={false}
            error={errors.physicalPostalCode}
          />
          <div className="col-span-2 flex gap-4 mt-2 mb-4">
            <Button variant="primary" onClick={copyPhysicalToCourier}>
              Copy Physical to Courier
            </Button>
            <Button variant="secondary" onClick={clearCourierDetails}>
              Clear Courier Details
            </Button>
          </div>
          <FormField
            id="courierAddress"
            label="Courier Address"
            type="text"
            value={formData.courierAddress || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Address"
            required={false}
            error={errors.courierAddress}
          />
          <FormField
            id="courierTown"
            label="Courier Town"
            type="text"
            value={formData.courierTown || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Town"
            required={false}
            error={errors.courierTown}
          />
          <FormField
            id="courierSuburb"
            label="Courier Suburb"
            type="text"
            value={formData.courierSuburb || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Suburb"
            required={false}
            error={errors.courierSuburb}
          />
          <FormField
            id="courierPostalCode"
            label="Courier Postal Code"
            type="text"
            value={formData.courierPostalCode || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Postal Code"
            required={false}
            error={errors.courierPostalCode}
          />
          <FormField
            id="isUnsubscribed"
            label="Unsubscribe"
            type="checkbox"
            value={formData.isUnsubscribed || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Is Unsubscribed"
            required={false}
            error={errors.isUnsubscribed}
            helper="Check to unsubscribe member from newsletters"
          />
        </FormSection>

        <FormSection id={FORM_SECTIONS.status.id} title={FORM_SECTIONS.status.title}>
          <FormField
            id="dateCertificateHunter"
            label="Certificate Date (Hunter)"
            type="date"
            value={formData.dateCertificateHunter || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.dateCertificateHunter}
          />

          <FormField
            id="dateCertificateSport"
            label="Certificate Date (Sport)"
            type="date"
            value={formData.dateCertificateSport || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.dateCertificateSport}
          />

          <FormField
            id="dateEnrolled"
            label="Date Enrolled"
            type="date"
            value={formatDateForInput(formData.dateEnrolled)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.dateEnrolled}
            helper="Date when the member was enrolled"
          />
          <FormField
            id="memberStatusId"
            label="Member Status"
            type="dropdown"
            value={formData.memberStatusId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Member Status"
            required={true}
            error={errors.memberStatusId}
            options={((memberStatusesData || []) as MemberStatusDTO[]).map((ms) => ({
              value: ms.id ?? '',
              label: (
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full inline-block"
                    style={{
                      backgroundColor: ms.colourGrading?.colour ?? '#ccc',
                    }}
                  />
                  <span>{ms.title ?? ''}</span>
                </div>
              ),
            }))}
            isLoading={isLoadingMemberStatuses}
          />
          <FormField
            id="isAlwaysCompliant"
            label="Always compliant"
            type="checkbox"
            value={formData.isAlwaysCompliant || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.isAlwaysCompliant}
            helper="Check to mark useer always as compliant"
          />
          <FormField
            id="isApprovalEmailSent"
            label="Approval email sent"
            type="checkbox"
            value={formData.isApprovalEmailSent || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.isApprovalEmailSent}
          />
        </FormSection>

        <FormSection
          id={FORM_SECTIONS.paymentDetails.id}
          title={FORM_SECTIONS.paymentDetails.title}
        >
          <FormField
            id="paidUpTo"
            label="Date paid up"
            type="date"
            value={formatDateForInput(formData.paidUpTo)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.paidUpTo}
          />

          {/* <FormField
            id="datePaidUpPrevious"
            label="Date paid (previous)"
            type="date"
            value={formatDateForInput(formData.datePaidUpPrevious)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.datePaidUpPrevious}
          /> */}

          <FormField
            id="paymentTypeId"
            label="Payment type"
            type="dropdown"
            value={formData.paymentTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Payment Type"
            required={false}
            error={errors.paymentTypeId}
            options={(paymentTypesData || []).map((pt) => ({
              value: pt.id ?? '',
              label: pt.title ?? '',
            }))}
            isLoading={isLoadingPaymentTypes}
          />

          <FormField
            id="isDebitOrderPayment"
            label="Is Debit Order Payment?"
            type="checkbox"
            value={formData.isDebitOrderPayment || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.isDebitOrderPayment}
            helper="Check if member is paying with a Debit Order"
          />

          {formData.proofOfPaymentFileId && (
            <ImageLink fileId={formData.proofOfPaymentFileId} label="Proof file" />
          )}
          <FileUpload
            id="proofOfPaymentFileUpload"
            onChange={handleFileChange('proofOfPaymentFileId')}
            title="Upload Proof file"
            accept={FileTypeConstants.ALL_SUPPORTED}
            required={false}
            error={errors.proofOfPaymentFileId}
            isUploading={uploadingFiles['proofOfPaymentFileId']}
          />
          {/* 
          <FormField
            id="amountPaid"
            label="Amount paid"
            type="number"
            value={formData.amountPaid || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="670.00"
            required={false}
            error={errors.amountPaid}
          /> */}

          {/* <FormField
            id="datePaid"
            label="Date payment made"
            type="date"
            value={formatDateForInput(formData.datePaid)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.datePaid}
          /> */}

          {/* <FormField
            id="dateInvoiceCreated"
            label="Date invoice created"
            type="date"
            value={formatDateForInput(formData.dateInvoiceCreated)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.dateInvoiceCreated}
          /> */}

          {/* <FormField
            id="dateInvoiceSent"
            label="Date invoice sent"
            type="date"
            value={formatDateForInput(formData.dateInvoiceSent)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.dateInvoiceSent}
          /> */}
          {/* 
          <FormField
            id="datePaymentDetailsSent"
            label="Date payment details sent"
            type="date"
            value={formatDateForInput(formData.datePaymentDetailsSent)}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.datePaymentDetailsSent}
          /> */}


        </FormSection>

        <FormSection id={FORM_SECTIONS.disciplinary.id} title={FORM_SECTIONS.disciplinary.title}>
          <FormField
            id="memberDisciplinaryStatusId"
            label="Disciplinary status"
            type="dropdown"
            value={formData.memberDisciplinaryStatusId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Member Disciplinary Status"
            required={true}
            error={errors.memberDisciplinaryStatusId}
            options={((disciplinaryStatusesData || []) as MemberDisciplinaryStatusDTO[]).map((ds) => ({
              value: ds.id ?? '',
              label: (
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full inline-block"
                    style={{
                      backgroundColor: ds.colourGrading?.colour ?? '#ccc',
                    }}
                  />
                  <span>{ds.title ?? ''}</span>
                </div>
              ),
            }))}
            isLoading={isLoadingDisciplinaryStatuses}
          />
          <FormField
            id="memberTerminationStatusId"
            label="Termination status"
            type="dropdown"
            value={formData.memberTerminationStatusId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Termination status"
            required={true}
            error={errors.memberTerminationStatusId}
            options={((terminationStatusesData || []) as MemberTerminationStatusDTO[]).map((ts) => ({
              value: ts.id ?? '',
              label: (
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full inline-block"
                    style={{
                      backgroundColor: ts.colourGrading?.colour ?? '#ccc',
                    }}
                  />
                  <span>{ts.title ?? ''}</span>
                </div>
              ),
            }))}
            isLoading={isLoadingTerminationStatuses}
          />
          <FormField
            id="dateDisciplinaryEnd"
            label="Date Disciplinary End"
            type="datetime"
            value={formData.dateDisciplinaryEnd || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select date"
            required={false}
            error={errors.dateDisciplinaryEnd}
          />
          <FormField
            id="disciplinaryReason"
            label="Disciplinary Reason"
            type="textarea"
            rows={4}
            value={formData.disciplinaryReason || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Disciplinary Reason"
            required={false}
            error={errors.disciplinaryReason}
          />
          <FormField
            id="isDistinguishedHidden"
            label="Hide distinguished"
            type="checkbox"
            value={formData.isDistinguishedHidden || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.isDistinguishedHidden}
            helper="Check to hide distinguished certificates"
          />
          <FormField
            id="isDedicatedStatusLapsed"
            label="Dedicated status lapsed"
            type="checkbox"
            value={formData.isDedicatedStatusLapsed || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.isDedicatedStatusLapsed}
            helper="Check to mark member as dedicated status lapsed"
          />
        </FormSection>

        <FormSection id={FORM_SECTIONS.general.id} title={FORM_SECTIONS.general.title}>
          <FormField
            id="memberRegistrationTypeId"
            label="Registration by"
            type="dropdown"
            value={formData.memberRegistrationTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Registered by"
            options={(registrationTypesData || []).map((rt) => ({
              value: rt.id ?? '',
              label: rt.title ?? '',
            }))}
            isLoading={isLoadingRegistrationTypes}
          />
          <FormField
            id="memberRegistrationStepId"
            label="Registration step"
            type="dropdown"
            value={formData.memberRegistrationStepId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Registration step"
            options={(registrationStepsData || []).map((rs) => ({
              value: rs.id ?? '',
              label: rs.title ?? '',
            }))}
            isLoading={isLoadingRegistrationSteps}
          />
          <FormField
            id="memberReferralTypeId"
            label="Referred by"
            type="dropdown"
            value={formData.memberReferralTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Referred by"
            options={(referralTypesData || []).map((rf) => ({
              value: rf.id ?? '',
              label: rf.title ?? '',
            }))}
            isLoading={isLoadingReferralTypes}
          />
          <FormField
            id="referredByName"
            label="Referred By Name"
            type="text"
            value={formData.referredByName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Referred By Name"
            required={false}
            error={errors.referredByName}
          />
          {formData.proofOfIdFileId && (
            <ImageLink fileId={formData.proofOfIdFileId} label="Proof Of Id File" />
          )}
          <FileUpload
            id="proofOfIdFileUpload"
            onChange={handleFileChange('proofOfIdFileId')}
            title="Upload Proof Of Id File"
            accept={FileTypeConstants.ALL_SUPPORTED}
            required={false}
            error={errors.proofOfIdFileId}
            isUploading={uploadingFiles['proofOfIdFileId']}
          />
          <FormField
            id="dateWelcomeEmailSent"
            label="Date Welcome Email Sent"
            type="datetime"
            value={formData.dateWelcomeEmailSent || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Date Welcome Email Sent"
            required={false}
            error={errors.dateWelcomeEmailSent}
          />
          <FormField
            id="dateConfirmEmailSent"
            label="Date Confirm Email Sent"
            type="datetime"
            value={formData.dateConfirmEmailSent || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Date Confirm Email Sent"
            required={false}
            error={errors.dateConfirmEmailSent}
          />
          <FormField
            id="dateMemberConfirmed"
            label="Date Member Confirmed"
            type="datetime"
            value={formData.dateMemberConfirmed || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Date Member Confirmed"
            required={false}
            error={errors.dateMemberConfirmed}
          />
          <FormField
            id="notes"
            label="Notes"
            type="textarea"
            rows={4}
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Notes"
            required={false}
            error={errors.notes}
          />
        </FormSection>

        <FormSection id={FORM_SECTIONS.history.id} title={FORM_SECTIONS.history.title}>
          <AuditLogTable
            data={auditLogs}
            currentPage={auditCurrentPage}
            totalPages={auditTotalPages}
            setCurrentPage={setAuditCurrentPage}
            pageSize={auditPageSize}
            setPageSize={setAuditPageSize}
            isLoading={isLoadingAuditLogs}
          />
        </FormSection>

        <div ref={emailSectionsRef}>
          {emailMessages.map((email) => (
            <FormSection
              key={email.id}
              id={FORM_SECTIONS.pastEmails.id}
              title={<EmailMessageItemHeader data={email} />}
              columns={1}
            >
              <EmailMessageItem data={email} />
            </FormSection>
          ))}
        </div>

        {emailMessages.length > 0 && emailTotalPages > 1 && (
          <Card>
            <Pagination
              currentPage={emailCurrentPage}
              totalPages={emailTotalPages}
              onPageChange={handleEmailPageChange}
              pageSize={emailPageSize}
              onPageSizeChange={handleEmailPageSizeChange}
            />
          </Card>
        )}
      </Form>

      {/* Password Change Drawer */}
      <ChangePasswordDrawer
        isOpen={isPasswordDrawerOpen}
        onClose={() => setIsPasswordDrawerOpen(false)}
        memberId={initialValues.id || ''}
        memberName={`${formData.firstName} ${formData.lastName}`.trim() || 'Member'}
      />
    </>
  );
};

export default MemberForm;
